const stripAnsi = (text: string) => {
  // 移除所有 ANSI 转义序列
  // eslint-disable-next-line no-control-regex
  return text.replace(/[\u001b\u009b][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g, '');
};

class ProcessOutput extends EventTarget {
  private buffer: string[] = [];

  private maxBufferSize: number;

  private isReading = false;

  private sourceStream: ReadableStream<string>;

  private abortController: AbortController;

  private onErrorStream: (error: string) => void;

  constructor(sourceStream: ReadableStream<string>, onErrorStream: (error: string) => void) {
    super();
    this.maxBufferSize = 1000;
    this.sourceStream = sourceStream;
    this.abortController = new AbortController();
    this.startReading();
    this.onErrorStream = onErrorStream;
  }

  private async startReading(): Promise<void> {
    if (this.isReading) return;
    this.isReading = true;

    const reader = this.sourceStream.getReader();

    try {
      while (true && !this.abortController.signal.aborted) {
        const { done, value } = await reader.read();

        if (done) break;

        if (value.startsWith('Error:') || value.indexOf('[22m [31m[1m[vite]') > -1) {
          this.onErrorStream(stripAnsi(value));
        }

        this.buffer.push(value);
        if (this.buffer.length > this.maxBufferSize) {
          this.buffer.shift();
        }

        this.dispatchEvent(new CustomEvent('data', { detail: value }));
      }
    } catch (error) {
      if (!this.abortController.signal.aborted) {
        this.dispatchEvent(new CustomEvent('error', { detail: error as Error }));
      }
    } finally {
      reader.releaseLock();
      if (!this.abortController.signal.aborted) {
        this.dispatchEvent(new CustomEvent('end'));
      }
      this.isReading = false;
    }
  }

  public getBuffer(): string[] {
    return [...this.buffer];
  }

  public createStream(): ReadableStream<string> {
    const buffer = this.getBuffer();
    let isActive = true;

    return new ReadableStream<string>({
      start: (controller: ReadableStreamDefaultController<string>) => {
        // 检查是否已经被清理
        if (this.abortController.signal.aborted) {
          controller.close();
          return;
        }

        // 发送历史数据
        buffer.forEach(chunk => {
          if (isActive && !this.abortController.signal.aborted) {
            controller.enqueue(chunk);
          }
        });

        const onData = (event: Event) => {
          const customEvent = event as CustomEvent<string>;
          if (isActive && !this.abortController.signal.aborted) {
            try {
              controller.enqueue(customEvent.detail);
            } catch (error) {
              console.error('Failed to enqueue data:', error);
            }
          }
        };

        const onEnd = () => {
          if (isActive) {
            try {
              controller.close();
            } catch (error) {
              console.error('Failed to close controller:', error);
            }
          }
        };

        const onError = (event: Event) => {
          const customEvent = event as CustomEvent<Error>;
          if (isActive) {
            try {
              controller.error(customEvent.detail);
            } catch (error) {
              console.error('Failed to error controller:', error);
            }
          }
        };

        const onAbort = () => {
          isActive = false;
          try {
            controller.close();
          } catch (error) {
            console.error('Failed to close controller on abort:', error);
          }
        };

        // 添加监听器
        this.addEventListener('data', onData, { signal: this.abortController.signal });
        this.addEventListener('end', onEnd, { signal: this.abortController.signal });
        this.addEventListener('error', onError, { signal: this.abortController.signal });
        this.abortController.signal.addEventListener('abort', onAbort);

        // 返回清理函数
        return () => {
          isActive = false;
        };
      },

      cancel() {
        isActive = false;
      },
    });
  }

  public cleanup(): void {
    this.buffer = [];
    this.abortController.abort();
  }

  // 提供类型安全的方法，但不重写 addEventListener
  public onData(listener: (event: CustomEvent<Uint8Array>) => void, options?: AddEventListenerOptions): void {
    this.addEventListener('data', listener as EventListener, options);
  }

  public onEnd(listener: (event: CustomEvent<void>) => void, options?: AddEventListenerOptions): void {
    this.addEventListener('end', listener as EventListener, options);
  }

  public onError(listener: (event: CustomEvent<Error>) => void, options?: AddEventListenerOptions): void {
    this.addEventListener('error', listener as EventListener, options);
  }

  public offData(listener: (event: CustomEvent<Uint8Array>) => void, options?: EventListenerOptions): void {
    this.removeEventListener('data', listener as EventListener, options);
  }

  public offEnd(listener: (event: CustomEvent<void>) => void, options?: EventListenerOptions): void {
    this.removeEventListener('end', listener as EventListener, options);
  }

  public offError(listener: (event: CustomEvent<Error>) => void, options?: EventListenerOptions): void {
    this.removeEventListener('error', listener as EventListener, options);
  }
}

export default ProcessOutput;
